import Phaser from "phaser";
import {
  advanceTrailSegments,
  cappedEdgeJitter,
  createTrailSegments,
  interpolatedEdgeJitterOrdered,
  sampleRibbonDistances,
  sortedTrail,
  variantFromSeed,
  RIBBON_MAX_SAMPLES,
  RIBBON_OVERSCAN_PX,
  TRAIL_DIRT,
  TRAIL_DIRT_DARK,
  TRAIL_DIRT_SHADOW,
  TRAIL_GRASS,
  type TrailSegment,
} from "../logic/trailSegments";
import { addBlob, ensurePrimitiveTextures, sizeBlob, TEX_PIXEL } from "../assets/primitives";
import { approachAlpha, distanceForProjectedY, type ProjectionContext } from "./RunnerProjection";

const SEGMENT_COUNT = 26;
const SEGMENT_LENGTH = 0.05;
const LANE_RUT_ALPHA = 0.16;
const WEAR_MARK_POOL = 120;
const WEAR_SIZE_EPS = 0.75;
const WEAR_ALPHA_EPS = 0.01;
const RUT_LANES = [-1, 0, 1] as const;

interface WearMark {
  sprite: Phaser.GameObjects.Image;
  lastColor: number;
  lastAlpha: number;
}

export class TrailRibbon {
  private readonly scene: Phaser.Scene;
  private segments: TrailSegment[];
  private readonly ordered: TrailSegment[] = [];
  private sampleCount = 0;
  private readonly sampleDistances: number[] = [];
  private readonly sampleY = (distance: number): number => this.projection.project(distance, 0).y;
  private readonly mid: Phaser.Math.Vector2[] = [];
  private readonly roadHalf: number[] = [];
  private readonly left: Phaser.Math.Vector2[] = [];
  private readonly right: Phaser.Math.Vector2[] = [];
  private readonly grassL: Phaser.Math.Vector2[] = [];
  private readonly grassR: Phaser.Math.Vector2[] = [];
  private readonly shadeL: Phaser.Math.Vector2[] = [];
  private readonly shadeR: Phaser.Math.Vector2[] = [];
  private readonly rutL: Phaser.Math.Vector2[] = [];
  private readonly rutR: Phaser.Math.Vector2[] = [];
  private readonly marks: WearMark[] = [];
  private markUsed = 0;
  private readonly grass: RibbonStrip;
  private readonly dirt: RibbonStrip;
  private readonly shadeLeft: RibbonStrip;
  private readonly shadeRight: RibbonStrip;
  private readonly ruts: RibbonStrip[] = [];

  constructor(
    scene: Phaser.Scene,
    private readonly projection: ProjectionContext,
  ) {
    this.scene = scene;
    ensurePrimitiveTextures(scene);

    const { width, height } = projection.frame;
    this.grass = new RibbonStrip(scene, TEX_PIXEL, 7, RIBBON_MAX_SAMPLES, width, height);
    this.dirt = new RibbonStrip(scene, TEX_PIXEL, 8, RIBBON_MAX_SAMPLES, width, height);
    this.shadeLeft = new RibbonStrip(scene, TEX_PIXEL, 8, RIBBON_MAX_SAMPLES, width, height);
    this.shadeRight = new RibbonStrip(scene, TEX_PIXEL, 8, RIBBON_MAX_SAMPLES, width, height);
    for (let i = 0; i < 3; i += 1) {
      this.ruts.push(new RibbonStrip(scene, TEX_PIXEL, 8, RIBBON_MAX_SAMPLES, width, height));
    }

    this.segments = createTrailSegments(
      SEGMENT_COUNT,
      SEGMENT_LENGTH,
      this.projection.frame.recycleDistance,
    );
    this.poolWearMarks();
    this.redraw();
  }

  layout(): void {
    const { width, height } = this.projection.frame;
    this.grass.resize(width, height);
    this.dirt.resize(width, height);
    this.shadeLeft.resize(width, height);
    this.shadeRight.resize(width, height);
    for (const rut of this.ruts) {
      rut.resize(width, height);
    }
    this.redraw();
  }

  update(deltaDistance: number): void {
    advanceTrailSegments(
      this.segments,
      deltaDistance,
      this.projection.frame.recycleDistance,
    );
    this.redraw();
  }

  private poolWearMarks(): void {
    for (let i = 0; i < WEAR_MARK_POOL; i += 1) {
      const sprite = addBlob(this.scene, 0, 0, 8, 4, TRAIL_DIRT, 0);
      sprite.setDepth(9).setVisible(false);
      this.marks.push({ sprite, lastColor: -1, lastAlpha: -1 });
    }
  }

  private redraw(): void {
    const ordered = sortedTrail(this.segments, this.ordered);
    if (ordered.length < 2) {
      this.hideRibbon();
      this.hideUnusedMarks();
      return;
    }

    const { frame } = this.projection;
    const nearest = ordered[0]!;
    const nearDistance = Math.min(
      nearest.distance,
      distanceForProjectedY(frame.height + RIBBON_OVERSCAN_PX, frame),
    );
    sampleRibbonDistances(
      this.segments,
      this.sampleY,
      {
        nearDistance,
        maxSamples: RIBBON_MAX_SAMPLES,
        ordered,
        into: this.sampleDistances,
      },
    );
    this.sampleCount = this.sampleDistances.length;
    if (this.sampleCount < 2) {
      this.hideRibbon();
      this.hideUnusedMarks();
      return;
    }

    for (let i = 0; i < this.sampleCount; i += 1) {
      this.pushEdges(this.sampleDistances[i]!, i, ordered);
    }

    this.grass.paint(this.grassL, this.grassR, this.sampleDistances, this.sampleCount, 0, 1, 0, TRAIL_GRASS, 0.5);
    this.dirt.paint(this.left, this.right, this.sampleDistances, this.sampleCount, 0, 1, 0, TRAIL_DIRT, 0.96);
    this.shadeLeft.paint(this.shadeL, this.left, this.sampleDistances, this.sampleCount, 0, 1, 0, TRAIL_DIRT_SHADOW, 0.3);
    this.shadeRight.paint(this.right, this.shadeR, this.sampleDistances, this.sampleCount, 0, 1, 0, TRAIL_DIRT_SHADOW, 0.3);
    this.drawLaneRuts();

    this.markUsed = 0;
    for (let i = ordered.length - 1; i >= 0; i -= 1) {
      this.drawWear(ordered[i]!);
    }
    this.hideUnusedMarks();
  }

  private hideRibbon(): void {
    this.grass.hide();
    this.dirt.hide();
    this.shadeLeft.hide();
    this.shadeRight.hide();
    for (const rut of this.ruts) {
      rut.hide();
    }
  }

  private pushEdges(distance: number, index: number, ordered: TrailSegment[]): void {
    const pose = this.projection.project(distance, 0);
    const jitter = cappedEdgeJitter(interpolatedEdgeJitterOrdered(ordered, distance), pose.roadHalfWidth);
    const half = pose.roadHalfWidth;
    this.at(this.mid, index, pose.x, pose.y);
    this.roadHalf[index] = half;
    this.at(this.left, index, pose.x - half + jitter, pose.y);
    this.at(this.right, index, pose.x + half - jitter * 0.85, pose.y);
    this.at(this.grassL, index, pose.x - half * 1.16 + jitter * 1.4, pose.y + 1);
    this.at(this.grassR, index, pose.x + half * 1.16 - jitter * 1.2, pose.y + 1);
    this.at(this.shadeL, index, pose.x - half * 0.84 + jitter, pose.y);
    this.at(this.shadeR, index, pose.x + half * 0.84 - jitter * 0.85, pose.y);
  }

  private drawLaneRuts(): void {
    const laneSpread = this.projection.frame.laneSpread;
    for (let laneIndex = 0; laneIndex < 3; laneIndex += 1) {
      const lane = RUT_LANES[laneIndex]!;
      const strip = this.ruts[laneIndex]!;
      for (let i = 0; i < this.sampleCount; i += 1) {
        const mid = this.mid[i]!;
        const roadHalfWidth = this.roadHalf[i]!;
        const x = mid.x + lane * roadHalfWidth * laneSpread;
        const half = Math.max(1.15, roadHalfWidth * 0.02);
        this.at(this.rutL, i, x - half, mid.y);
        this.at(this.rutR, i, x + half, mid.y);
      }
      strip.paint(this.rutL, this.rutR, this.sampleDistances, this.sampleCount, 0, 1, 0, TRAIL_DIRT_DARK, LANE_RUT_ALPHA);
    }
  }

  private drawWear(segment: TrailSegment): void {
    const variant = variantFromSeed(segment.seed);
    const fade = approachAlpha(segment.distance + segment.length * 0.4, this.projection.frame) * 0.7;
    if (fade < 0.04) {
      return;
    }

    const mid = this.projection.project(segment.distance + segment.length * 0.5, variant.rutWander * 0.18);
    this.stamp(mid.x, mid.y, 40 * mid.scale * 7, 10 * mid.scale * 5, variant.dirtColor, 0.1 + variant.shade * 0.08, mid.scale);

    if (variant.shade > 0.45) {
      const shade = this.projection.project(segment.distance + segment.length * 0.35, variant.patchLane * 0.35);
      this.stamp(shade.x, shade.y, 28 * shade.scale * 6, 9 * shade.scale * 4, TRAIL_DIRT_SHADOW, 0.1 * fade, shade.scale);
    }

    if (variant.hasPatch) {
      const patch = this.projection.project(segment.distance + segment.length * 0.42, variant.patchLane * 0.55);
      this.stamp(patch.x, patch.y, 22 * patch.scale * 6, 8 * patch.scale * 4, TRAIL_DIRT_DARK, 0.14 * fade, patch.scale);
    }
    if (variant.hasLeaves) {
      const leaf = this.projection.project(segment.distance + segment.length * 0.7, variant.patchLane * 0.4);
      this.stamp(leaf.x, leaf.y, 8 * leaf.scale * 5, 4 * leaf.scale * 3, 0xb85a28, 0.22 * fade, leaf.scale);
      this.stamp(leaf.x + 7 * leaf.scale, leaf.y + 2, 6 * leaf.scale * 4, 3 * leaf.scale * 3, 0xc4a056, 0.16 * fade, leaf.scale);
    }
    if (variant.hasStones) {
      const stone = this.projection.project(segment.distance + segment.length * 0.28, -variant.patchLane * 0.45);
      this.stamp(stone.x, stone.y, 5 * stone.scale * 4, 3 * stone.scale * 3, 0x8a8680, 0.28 * fade, stone.scale);
      this.stamp(stone.x + 8 * stone.scale, stone.y + 1, 3.5 * stone.scale * 3, 2.2 * stone.scale * 2, 0x6e6a64, 0.18 * fade, stone.scale);
    }

    const tuft = this.projection.project(segment.distance + segment.length * 0.6, (variant.edgeJitter >= 0 ? 1 : -1) * 1.02);
    this.stamp(tuft.x, tuft.y, 16 * tuft.scale * 5, 6 * tuft.scale * 3, TRAIL_GRASS, 0.2 * fade, tuft.scale);
  }

  private stamp(
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    alpha: number,
    scale: number,
  ): void {
    const mark = this.marks[this.markUsed];
    if (!mark) {
      return;
    }
    const shrink = nearBlobShrink(scale);
    const nextW = width * shrink;
    const nextH = height * shrink;
    const sprite = mark.sprite;
    if (!sprite.visible) {
      sprite.setVisible(true);
    }
    sprite.setPosition(x, y);
    if (Math.abs(sprite.displayWidth - nextW) >= WEAR_SIZE_EPS || Math.abs(sprite.displayHeight - nextH) >= WEAR_SIZE_EPS * 0.7) {
      sizeBlob(sprite, nextW, nextH);
    }
    if (color !== mark.lastColor) {
      mark.lastColor = color;
      sprite.setTint(color);
    }
    if (Math.abs(alpha - mark.lastAlpha) >= WEAR_ALPHA_EPS) {
      mark.lastAlpha = alpha;
      sprite.setAlpha(alpha);
    }
    this.markUsed += 1;
  }

  private hideUnusedMarks(): void {
    for (let i = this.markUsed; i < this.marks.length; i += 1) {
      const sprite = this.marks[i]!.sprite;
      if (sprite.visible) {
        sprite.setVisible(false);
      }
    }
  }

  private at(pool: Phaser.Math.Vector2[], index: number, x: number, y: number): void {
    const existing = pool[index];
    if (existing) {
      existing.set(x, y);
      return;
    }
    pool[index] = new Phaser.Math.Vector2(x, y);
  }
}

class RibbonStrip {
  readonly mesh: Phaser.GameObjects.Mesh;
  private readonly maxQuads: number;
  private usedQuads = 0;

  constructor(scene: Phaser.Scene, textureKey: string, depth: number, maxSamples: number, width: number, height: number) {
    this.maxQuads = Math.max(1, maxSamples - 1);
    const vertices: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    for (let q = 0; q < this.maxQuads; q += 1) {
      const i = q * 4;
      vertices.push(0, 0, 0, 0, 0, 0, 0, 0);
      uvs.push(0, 0, 1, 0, 0, 1, 1, 1);
      indices.push(i, i + 2, i + 1, i + 2, i + 3, i + 1);
    }

    this.mesh = scene.add.mesh(width * 0.5, height * 0.5, textureKey);
    this.mesh.addVertices(vertices, uvs, indices);
    this.mesh.hideCCW = false;
    this.mesh.ignoreDirtyCache = true;
    this.mesh.setSize(width, height);
    this.mesh.setOrtho(width, height);
    this.mesh.setDepth(depth);
  }

  resize(width: number, height: number): void {
    this.mesh.setPosition(width * 0.5, height * 0.5);
    this.mesh.setSize(width, height);
    this.mesh.setOrtho(width, height);
  }

  hide(): void {
    this.paint([], [], [], 0, 0, 1, 0, 0xffffff, 0);
  }

  paint(
    left: Phaser.Math.Vector2[],
    right: Phaser.Math.Vector2[],
    distances: number[],
    sampleCount: number,
    u0: number,
    u1: number,
    vScale: number,
    color: number,
    alpha: number,
  ): void {
    const originX = this.mesh.x;
    const originY = this.mesh.y;
    const quads = Math.max(0, Math.min(this.maxQuads, sampleCount - 1));
    const faces = this.mesh.faces;

    for (let q = 0; q < quads; q += 1) {
      const faceA = faces[q * 2];
      const faceB = faces[q * 2 + 1];
      if (!faceA || !faceB) {
        continue;
      }

      const l0 = left[q]!;
      const r0 = right[q]!;
      const l1 = left[q + 1]!;
      const r1 = right[q + 1]!;
      const v0 = distances[q]! * vScale;
      const v1 = distances[q + 1]! * vScale;

      writeVertex(faceA.vertex1, l0.x, l0.y, originX, originY, u0, v0, color, alpha);
      writeVertex(faceA.vertex2, l1.x, l1.y, originX, originY, u0, v1, color, alpha);
      writeVertex(faceA.vertex3, r0.x, r0.y, originX, originY, u1, v0, color, alpha);
      writeVertex(faceB.vertex1, l1.x, l1.y, originX, originY, u0, v1, color, alpha);
      writeVertex(faceB.vertex2, r1.x, r1.y, originX, originY, u1, v1, color, alpha);
      writeVertex(faceB.vertex3, r0.x, r0.y, originX, originY, u1, v0, color, alpha);
    }

    for (let q = quads; q < this.usedQuads; q += 1) {
      const faceA = faces[q * 2];
      const faceB = faces[q * 2 + 1];
      if (!faceA || !faceB) {
        continue;
      }
      writeVertex(faceA.vertex1, originX, originY, originX, originY, 0, 0, color, 0);
      writeVertex(faceA.vertex2, originX, originY, originX, originY, 0, 0, color, 0);
      writeVertex(faceA.vertex3, originX, originY, originX, originY, 0, 0, color, 0);
      writeVertex(faceB.vertex1, originX, originY, originX, originY, 0, 0, color, 0);
      writeVertex(faceB.vertex2, originX, originY, originX, originY, 0, 0, color, 0);
      writeVertex(faceB.vertex3, originX, originY, originX, originY, 0, 0, color, 0);
    }

    this.usedQuads = quads;
  }
}

function writeVertex(
  vertex: Phaser.Geom.Mesh.Vertex,
  screenX: number,
  screenY: number,
  originX: number,
  originY: number,
  u: number,
  v: number,
  color: number,
  alpha: number,
): void {
  vertex.x = screenX - originX;
  vertex.y = originY - screenY;
  vertex.z = 0;
  vertex.setUVs(u, v);
  vertex.color = color;
  vertex.alpha = alpha;
}

function nearBlobShrink(scale: number): number {
  if (scale <= 0.32) {
    return 1;
  }
  return Math.max(0.45, 1 - (scale - 0.32) * 1.2);
}
